'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { ChatBubble } from './chat-bubble';
import { ChatInput } from './chat-input';
import { Message } from './message';
import { CheckCircle2Icon, SparklesIcon, ChatAIIcon } from './chatbot-icons';
import { ChatbotProps, ChatMessage } from './models';
import { CHATBOT_TEXT } from './text';
import { chatStorage } from './chat-storage';

const WELCOME_MESSAGE = CHATBOT_TEXT.welcomeMessage;
const RAG_FEATURES = CHATBOT_TEXT.features;
const style = `
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `;

export function Chatbot({ title = CHATBOT_TEXT.title }: ChatbotProps) {
    const wsRef = useRef<WebSocket | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showLeadForm, setShowLeadForm] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [showEndChatConfirmation, setShowEndChatConfirmation] = useState(false);
    const [isEndingChat, setIsEndingChat] = useState(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Scroll to bottom when chatbot opens and there are messages
    useEffect(() => {
        if (isOpen && messages.length > 0) {
            // Use setTimeout to ensure the DOM has rendered
            setTimeout(() => {
                scrollToBottom();
            }, 100);
        }
    }, [isOpen, messages.length]);

    // Load chat history and session ID from sessionStorage on component mount
    useEffect(() => {
        const storedMessages = chatStorage.load();
        const storedSessionId = chatStorage.getSessionId();

        if (storedSessionId) {
            setSessionId(storedSessionId);
        }

        if (storedMessages.length > 0) {
            setMessages(storedMessages);
        } else {
            // Set welcome message only if no stored messages
            setMessages([
                {
                    id: '1',
                    content: WELCOME_MESSAGE,
                    sender: 'ai',
                    timestamp: new Date(),
                },
            ]);
        }
    }, []);

    // Save messages to sessionStorage whenever messages change
    useEffect(() => {
        if (messages.length > 0) {
            chatStorage.save(messages);
        }
    }, [messages]);

    // WebSocket connection management
    const connectWebSocket = useCallback(() => {
        const wsUrl = process.env.NEXT_PUBLIC_CHATBOT_WS_URL;

        if (!wsUrl) {
            console.error('WebSocket URL not configured');
            return;
        }

        try {
            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {};

            wsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    // Save session_id if it's provided in the response
                    if (data.session_id && !sessionId) {
                        setSessionId(data.session_id);
                        chatStorage.saveSessionId(data.session_id);
                    }

                    // Handle end chat response with thank you message
                    if (typeof data === 'string' && data?.toLowerCase().includes('thank you')) {
                        const thankYouMessage: ChatMessage = {
                            id: Date.now().toString(),
                            content: data || 'Thank you for chatting with us!',
                            sender: 'ai',
                            timestamp: new Date(),
                        };

                        // Add thank you message
                        setMessages((prev) => [...prev, thankYouMessage]);

                        // After a brief delay, reset everything
                        setTimeout(() => {
                            // Reset chat state
                            setMessages([
                                {
                                    id: '1',
                                    content: WELCOME_MESSAGE,
                                    sender: 'ai',
                                    timestamp: new Date(),
                                },
                            ]);

                            // Clear session storage and session ID
                            chatStorage.clearAll();
                            setSessionId(null);
                            setShowLeadForm(false);
                            setIsEndingChat(false);
                        }, 4000); // Show thank you message for 4 seconds

                        return;
                    }

                    const aiMessage: ChatMessage = {
                        id: Date.now().toString(),
                        content: data.response || data || CHATBOT_TEXT.errorMessage,
                        sender: 'ai',
                        timestamp: new Date(),
                    };

                    setMessages((prev) => [...prev, aiMessage]);
                    setIsLoading(false);
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                    setIsLoading(false);
                }
            };

            wsRef.current.onclose = () => {};

            wsRef.current.onerror = (error) => {
                console.error('WebSocket error:', error);
                setIsLoading(false);
            };
        } catch (error) {
            console.error('Error connecting to WebSocket:', error);
        }
    }, [sessionId]);

    // Initialize WebSocket connection
    useEffect(() => {
        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [connectWebSocket]);

    const handleSendMessage = async (message: string, file?: File) => {
        // Add user message
        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            content: message,
            sender: 'user',
            timestamp: new Date(),
            fileName: file?.name,
        };

        setMessages((prev) => [...prev, userMessage]);
        setIsLoading(true);

        try {
            // Send message via WebSocket
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                const payload: any = {
                    message: message,
                };

                // Include session_id if it exists
                if (sessionId) {
                    payload.session_id = sessionId;
                }

                wsRef.current.send(JSON.stringify(payload));
            } else {
                // WebSocket not connected, show error
                const errorMessage: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    content: CHATBOT_TEXT.connectionErrorMessage,
                    sender: 'ai',
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, errorMessage]);
                setIsLoading(false);

                // Try to reconnect
                connectWebSocket();
            }

            // Show lead form after 2nd AI message
            if (messages.length > 3 && !showLeadForm) {
                setShowLeadForm(true);
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                content: CHATBOT_TEXT.processingErrorMessage,
                sender: 'ai',
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
            setIsLoading(false);
        }
    };

    const handleEndChat = () => {
        setShowEndChatConfirmation(true);
    };

    const handleConfirmEndChat = async () => {
        try {
            setShowEndChatConfirmation(false);
            setIsEndingChat(true);

            // Send end chat payload via WebSocket if connected and session exists
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && sessionId) {
                const payload = {
                    action: 'end_chat',
                    session_id: sessionId,
                };

                wsRef.current.send(JSON.stringify(payload));
            } else {
                // If no WebSocket connection, just reset immediately
                setTimeout(() => {
                    // Reset chat state
                    setMessages([
                        {
                            id: '1',
                            content: WELCOME_MESSAGE,
                            sender: 'ai',
                            timestamp: new Date(),
                        },
                    ]);

                    // Clear session storage and session ID
                    chatStorage.clearAll();
                    setSessionId(null);
                    setShowLeadForm(false);
                    setIsEndingChat(false);
                }, 1000);
            }
        } catch (error) {
            console.error('Error ending chat:', error);
            setIsEndingChat(false);
        }
    };

    const handleCancelEndChat = () => {
        setShowEndChatConfirmation(false);
    };

    // Handle escape key to close confirmation popup
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showEndChatConfirmation) {
                handleCancelEndChat();
            }
        };

        if (showEndChatConfirmation) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [showEndChatConfirmation]);

    return (
        <>
            {/* Chat Bubble */}
            <ChatBubble onToggle={setIsOpen} isOpen={isOpen} />

            {/* Chat Window */}
            {isOpen && (
                <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-32px)] h-[70vh] max-h-[600px] rounded-2xl shadow-2xl flex flex-col z-50 border border-gray-200 overflow-hidden bg-white">
                    {/* Premium Header with Logo */}
                    <div className="bg-[#05a6f0] text-white p-5 rounded-t-2xl">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <h3 className="text-lg font-bold mb-1">{title}</h3>
                                <div className="text-white text-xs leading-relaxed">{CHATBOT_TEXT.premiumHeaderText}</div>
                            </div>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white/95 backdrop-blur-sm">
                        {messages.map((msg) => (
                            <Message key={msg.id} content={msg.content} sender={msg.sender} timestamp={msg.timestamp} />
                        ))}

                        {isLoading && (
                            <div className="flex justify-start gap-3 animate-fade-in">
                                <div
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#05a6f0]"
                                    aria-hidden
                                >
                                    <ChatAIIcon className="h-5 w-5" size={20} />
                                </div>
                                <div className="bg-white px-4 py-3 rounded-xl rounded-bl-none shadow-sm border border-gray-100">
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 bg-[#05a6f0] rounded-full animate-bounce"></div>
                                        <div
                                            className="w-2 h-2 bg-[#05a6f0] rounded-full animate-bounce"
                                            style={{ animationDelay: '0.1s' }}
                                        ></div>
                                        <div
                                            className="w-2 h-2 bg-[#05a6f0] rounded-full animate-bounce"
                                            style={{ animationDelay: '0.2s' }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* RAG Capability Showcase - Only show initially */}
                        {messages.length === 1 && !isLoading && (
                            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-[#05a6f0]/20 rounded-xl p-4 mt-4 animate-fade-in">
                                <div className="flex items-center gap-2 mb-3">
                                    <SparklesIcon className="w-4 h-4 text-[#05a6f0]" />
                                    <div className="text-sm font-bold text-black">{CHATBOT_TEXT.ragCapabilities}</div>
                                </div>
                                <div className="space-y-2">
                                    {RAG_FEATURES.map((feature, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <CheckCircle2Icon className="w-4 h-4 text-[#05a6f0] flex-shrink-0" />
                                            <span className="text-black font-medium">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* End Chat Button - Only show after chat has started (more than welcome message) */}
                        {!isLoading && messages.length > 1 && !isEndingChat && (
                            <div className="flex justify-center pt-2 pb-1">
                                <button
                                    type="button"
                                    onClick={handleEndChat}
                                    className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors border border-gray-200 hover:border-primary-200"
                                >
                                    End Chat
                                </button>
                            </div>
                        )}

                        {/* Ending Chat Loading State */}
                        {isEndingChat && (
                            <div className="flex justify-center pt-2 pb-1">
                                <div className="px-3 py-1.5 text-xs font-medium text-gray-500 rounded-full border border-gray-200 flex items-center gap-2">
                                    <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                    {CHATBOT_TEXT.endingChat}
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <ChatInput onSendMessage={handleSendMessage} isLoading={isLoading} />

                    {/* End Chat Confirmation Popup - Now inside chatbot container */}
                    {showEndChatConfirmation && (
                        <div
                            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 p-4 rounded-2xl"
                            onClick={handleCancelEndChat}
                        >
                            <div
                                className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 animate-fade-in"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6">
                                    <h3 className="text-base font-semibold text-gray-900 mb-3">End Chat</h3>
                                    <div className="text-gray-600 mb-6 text-sm">{CHATBOT_TEXT.endChatConfirmation}</div>
                                    <div className="flex gap-3 justify-end">
                                        <button
                                            type="button"
                                            onClick={handleCancelEndChat}
                                            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                        >
                                            {CHATBOT_TEXT.endChatCancel}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleConfirmEndChat}
                                            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                                        >
                                            {CHATBOT_TEXT.endChatYes}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* CSS for animations */}
            <style>{style}</style>
        </>
    );
}
